import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from "react-dom";
import styled from 'styled-components'
import { Table } from 'antd';
import { Icon, ThemeProvider } from 'dlweb-components';
import _ from 'lodash';
import ContextMenu from '../../ContextMenu';

const GroupTable = ({groups, loading, selectedGroups, setSelectedGroups, pagination, sorter, filter, onContextClick, handleTableChange, defaultGroups, getColumnSearchProps}) => {

  const [menuCoordinates, setMenuCoordinates] = useState({});
  const [portalContainer, setPortalContainer] = useState(null);

  const rowSelection = {
    selectedRowKeys: selectedGroups.map(o => o.id),
  };

  useEffect(() => {
    // create div for context menu
    const el = document.createElement("div");
    document.body.appendChild(el);
    setPortalContainer(el);

    return () => {
      // remove div when component unmounts
      document.body.removeChild(el);
    };
  }, []);

  const onRowClick = (e, record, index) => {

    let clearSelection = true;
    let selected = true;

    if(e.ctrlKey) {
      if(selectedGroups.length !== 1 && _.find(selectedGroups, record)) {
        selected = false;
      }

      clearSelection = false;
    }
    else if (e.shiftKey) {
      if(selectedGroups.length === 1 && selectedGroups[0].id === record.id) return;
      // Handle Shift key selection logic
      const lastSelectedIndex = selectedGroups.length > 0 ? selectedGroups[selectedGroups.length - 1].index : -1;
      const currentIndex = index; // Assuming record has an index property

      let range = [];
      if (lastSelectedIndex < currentIndex) {
          for (let i = lastSelectedIndex + 1; i <= currentIndex; i++) {
              range.push(groups[i]); // Assuming data is the array of all rows
          }
      } else {
          for (let i = currentIndex; i <= lastSelectedIndex - 1; i++) {
              range.push(groups[i]);
          }
      }

      setSelectedGroups([...selectedGroups, ...range], false, range);
      return;
    }
    else {
      if(_.find(selectedGroups, record) && selectedGroups.length === 1) return;
    }

    if (e.target.className.indexOf('ant-checkbox') > -1) return;
    
    setSelectedGroups(selected, clearSelection, [record])
  }
 
  const contextMenu = [{
    key: 'delete-group',
    action: 'delete-group',
    icon: <Icon type='users-slash' fixedWidth="25px" />,
    label: `Delete Group${selectedGroups.length > 1 ? 's' : ''}`
  }]

  const openContextMenu = useCallback((e, record) => {

    e.preventDefault();

    const isSelected = e.target.closest('tr').className.indexOf('ant-table-row-selected') > -1;
    
    setSelectedGroups(true, !isSelected, [record]);
    
    let coordinates = { x: e.pageX, y: e.pageY };

    const isDefaultGroup = defaultGroups.includes(record.id);
    const isDefaultSelected = _.map(selectedGroups, 'id').some(i => defaultGroups.includes(i));

    if ((isDefaultGroup && !isSelected) || (isSelected && isDefaultSelected)) {
      coordinates = {};  
    }

    setMenuCoordinates(coordinates);
  }, [selectedGroups, defaultGroups, setSelectedGroups])

  const columns = [
    {
      key: 'ID',
      title: 'Group Name',
      dataIndex: 'id',
      sorter: true,
      defaultSortOrder: 'ascend',
      sortOrder: sorter.columnKey === 'ID' ? sorter.order : null,
      filteredValue: filter.ID || null,
      render: (text) =>
        <GroupName>
          <Icon type='users' fixedWidth="25px" style={{marginRight: '10px', color: '#99a1a8'}} />
          {text}
        </GroupName>,
      ...getColumnSearchProps('Group Name', 'id'),
    },
  ];

  return(
    <ThemeProvider componentToken={{Table: { headerBorderRadius: 0}}}>
      <TableContainer total={pagination.total} pageSize={pagination.defaultPageSize}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={groups}
          columns={columns}
          pagination={{
            ...pagination,
            defaultCurrent: 1,
            defaultPageSize: 500,
            showSizeChanger: false,
            hideOnSinglePage: true,
            position: ['bottomCenter'],
          }}
          showSorterTooltip={false}
          size="middle"
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
          onChange={handleTableChange}
          onRow={(record, rowIndex) => {
            return {
              onClick: event => onRowClick(event, record, rowIndex),
              onContextMenu: event => openContextMenu(event, record),
            };
          }}
          scroll={{y: 'calc(100vh - 250px)'}}
        />
        {portalContainer && 
          createPortal(
            <ContextMenu
              menuItems={contextMenu}
              coordinates={menuCoordinates}
              handleClose={() => setMenuCoordinates({})}
              onClick={onContextClick}
            />,
            portalContainer
          )
        }
      </TableContainer>
    </ThemeProvider>
    )
}

const TableContainer = styled.div`
  margin-left: -32px;
  user-select: none;
  .ant-table-body,
  .ant-table-tbody,
  .ant-table-content,
  .ant-table-container,
  .ant-table-wrapper,
  .ant-table-placeholder,
  .ant-table-expanded-row-fixed {
    overflow: hidden auto !important;
    min-height: ${p => p.total < p.pageSize ? 'calc(100vh - 186px)' : 'calc(100vh - 241px)'} !important;
  }
  td.ant-table-column-sort {
    background: #fff;
  }
  .ant-table-cell:has(.ant-empty-normal) {
    border-bottom: none !important;
  }
`

const GroupName = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding-right: 10px;
`

export default GroupTable;